import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { useState } from 'react';
import { OknoPotwierdzenia } from '@/components/OknoPotwierdzenia';
import { WynikWylaczeniaSpsSync } from '@/components/WynikWylaczeniaSpsSync';
import { useToast } from '@/components/ui/toast';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter } from '@/components/ui/dialog';
import { useI18n } from '@/lib/i18n';
import {
  przerwijWylaczanieSledzenia,
  wylaczCt,
  wylaczSpsSync,
  type AgentRow,
  type PodsumowanieSledzenia,
  type SledzenieSurowe,
} from '@/lib/agents-api';

/**
 * CO ŚLEDZI ZMIANY W BAZIE TEGO WĘZŁA — i ile to zajmuje.
 *
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *  ⚠️⚠️ ZAMÓWIONE 02.09.2026: „fajnie jakbyśmy wiedzieli, gdzie to chodzi, a nie powinno, bo to
 *  chyba dość zjada bazę i kompy zwalniają". Portal odpowiada na trzy pytania naraz: CO chodzi,
 *  GDZIE (lista tabel w okienku) i ILE ZAJMUJE.
 *
 *  ⚠️⚠️ CZTERY MECHANIZMY, NIE TRZY — i to jest poprawka z 03.09.2026, po zajrzeniu do kopii
 *  bazy klienta. Do tego dnia ekran znał Change Tracking, CDC i bezimienne „triggery", a przy
 *  wyłączonych CT/CDC pisał „Zajmuje 0.0 MB". Na tej samej bazie stało 471 MB
 *  `SyncChangeTracking` i 131 czynnych triggerów, które ją napełniają.
 *
 *  ⚠️ „SYNCCHANGETRACKING" TO NIE „CHANGE TRACKING". Pierwsze to WŁASNA replikacja SupplyPointa
 *  (triggery `SYNC_TRIGGER_*` plus tabele `Sync*`), drugie to mechanizm MSSQL-a. Dwie różne
 *  rzeczy o niemal tej samej nazwie, o różnym koszcie i różnym sposobie wyłączania — dlatego
 *  każda ma tu własną plakietkę i ekran mówi wprost, że to nie to samo.
 *
 *  ⚠️ MY Z ŻADNEGO Z NICH NIE KORZYSTAMY i ekran ma to powiedzieć. Agent liczy różnicę lokalnie,
 *  więc to koszt bez zysku — a bez tego zdania czytelnik mógłby uznać, że to nasza konfiguracja
 *  i lepiej jej nie ruszać.
 *
 *  ⚠️ „NIE ZMIERZONO" ≠ „0 B", a „nie szukaliśmy" ≠ „nie ma". Konto agenta u klienta ma tylko
 *  prawa czytania, a węzeł w chmurze nie ma SupplyPointa wcale.
 *
 *  ⚠️⚠️ WYŁĄCZENIE IDZIE Z PORTALU (03.09.2026) i to jest jedyny przycisk na tym ekranie, który
 *  ZATRZYMUJE AUTOMAT: agent wyłącza SPSUI, uruchamia `spsdbupdate.exe -removereplication`
 *  (tylko to narzędzie ma prawa zdjąć triggery — zmierzone: naszemu kontu odmawia i `TRUNCATE`,
 *  i `DROP TRIGGER`), czyści tabele partiami i podnosi interfejs. Procedura w
 *  `agent/src/zadania/sps-sync-wylacz.ts`, warunki dopuszczenia w `api/.../wylaczenie-mozliwe.ts`.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 */
export function SledzenieBazy({
  podsumowanie,
  surowe,
  zmierzono,
  nodeId,
  agentId,
  groupId,
  wylaczenie,
  wylaczenieCt,
}: {
  podsumowanie: PodsumowanieSledzenia | null;
  surowe: SledzenieSurowe | null;
  /** Kiedy agent to zmierzył; `null` = nie mierzył (albo jest starszy niż ten pomiar). */
  zmierzono: string | null;
  nodeId: string;
  /** Bez identyfikatora węzła i wyspy przycisk wyłączenia się nie pokazuje. */
  agentId?: string;
  groupId?: string;
  /** Ostatnie zadanie wyłączenia replikacji; `null` = nigdy nie zlecono. */
  wylaczenie?: AgentRow['wylaczenieSpsSync'];
  /** Ostatnie zadanie wyłączenia natywnego CT — osobne zadanie, osobny wynik. */
  wylaczenieCt?: AgentRow['wylaczenieCt'];
}) {
  const { t } = useI18n();
  const qc = useQueryClient();
  const toast = useToast();
  const [tabeleOtwarte, setTabeleOtwarte] = useState(false);
  const [pytamOWylaczenie, setPytamOWylaczenie] = useState(false);
  const [pytamOCt, setPytamOCt] = useState(false);

  /**
   * ⚠️⚠️ HOOKI NAD KAŻDYM `return` — wyjście „brak pomiaru" stoi kilka linii niżej, a hook
   * postawiony za nim zmienia ich liczbę między renderami i React wywala CAŁE drzewo na biały
   * ekran. Ta klasa błędu położyła już zakładkę Moduły (02.09.2026); pilnuje jej
   * `hooki-nad-returnami.spec.ts`.
   */
  const wylaczanie = useMutation({
    mutationFn: ({ agent, grupa }: { agent: string; grupa: string }) => wylaczSpsSync(agent, grupa),
    onSuccess: () => {
      setPytamOWylaczenie(false);
      toast.success(t('sledz.wylaczZlecone'));
      void qc.invalidateQueries({ queryKey: ['agents'] });
      void qc.invalidateQueries({ queryKey: ['machine-tasks'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  /** ⚠️ HOOK NAD `return`, tak jak ten wyżej — patrz komentarz przy `wylaczanie`. */
  const wylaczanieCt = useMutation({
    mutationFn: ({ agent, grupa }: { agent: string; grupa: string }) => wylaczCt(agent, grupa),
    onSuccess: () => {
      setPytamOCt(false);
      toast.success(t('sledz.wylaczCtZlecone'));
      void qc.invalidateQueries({ queryKey: ['agents'] });
      void qc.invalidateQueries({ queryKey: ['machine-tasks'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  /** ⚠️ HOOK NAD `return`, tak jak dwa wyżej — patrz komentarz przy `wylaczanie`. */
  const przerwanie = useMutation({
    mutationFn: ({ agent, grupa }: { agent: string; grupa: string }) => przerwijWylaczanieSledzenia(agent, grupa),
    onSuccess: (w) => {
      /** ⚠️ POKAZUJEMY ZDANIE Z SERWERA, nie własne „zrobione" — ono mówi, KIEDY interfejs wstanie. */
      toast.success(w.zdanie);
      void qc.invalidateQueries({ queryKey: ['agents'] });
      void qc.invalidateQueries({ queryKey: ['machine-tasks'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (podsumowanie === null || surowe === null || zmierzono === null) {
    return <p className="text-[11px] text-muted-foreground">{t('sledz.brakPomiaru')}</p>;
  }

  /**
   * ══════════════════════════════════════════════════════════════════════════════════
   *  JEDNO WYŁĄCZENIE NARAZ — LICZONE NA EKRANIE Z TYCH SAMYCH DANYCH, CO NA SERWERZE.
   *
   *  ⚠️⚠️ POPRAWKA PO AWARII NA PRODUKCJI (09.09.2026, PESA): „Kamil włączył oba jednocześnie
   *  i dość mocno się rozjebało". Oba przyciski były wyszarzane WYŁĄCZNIE na czas własnej
   *  mutacji (`isPending`), czyli na sekundę wysyłki żądania — a nie na czas trwania zadania.
   *  Po kliknięciu jednego drugi natychmiast wracał do klikalności.
   *
   *  ⚠️ SERWER I TAK ODMÓWI (`jedno-wylaczenie-naraz.ts`), ale ekran ma pokazywać ten sam
   *  warunek: przycisk, który wygląda na czynny i zawodzi po kliknięciu, uczy klikać dalej.
   * ══════════════════════════════════════════════════════════════════════════════════
   */
  const zalegle = (z: { status: string } | null | undefined): boolean =>
    z != null && (z.status === 'QUEUED' || z.status === 'SENT' || z.status === 'RUNNING');
  const cosLeci = zalegle(wylaczenie) || zalegle(wylaczenieCt);
  /** ⚠️ Powód wyszarzenia MUSI być widoczny — inaczej martwy przycisk wygląda na awarię portalu. */
  const powodJednoNaraz = cosLeci ? t('sledz.jednoNaraz') : undefined;

  const p = podsumowanie;
  const objete = surowe.tabele.filter((x) => x.ct || x.cdc);
  const sps = p.spsSync ?? null;
  const spsSurowe = surowe.spsSync;
  /** Liczba pod przyciskiem musi zgadzać się z tym, co okienko FAKTYCZNIE wypisze. */
  const doPokazania = objete.length + (spsSurowe?.tabele.length ?? 0) + (sps?.piszacy.length ?? 0);

  return (
    <div className="space-y-2">
      {/*
        ⚠️⚠️ JEDEN BLOK NA MECHANIZM, W STAŁEJ KOLEJNOŚCI — i to jest poprawka po zgłoszeniu
        (03.09.2026): „teraz mamy na odwrót pokazywanie CT / SCT (i jeszcze CDC pomiędzy), a pod
        spodem najpierw jest wyłączenie SCT a potem dopiero CT i mamy na krzyż opcje różne".

        Poprzedni układ miał WSZYSTKIE plakietki w jednym rzędzie, a WSZYSTKIE przyciski w drugim —
        i te dwa rzędy szły w innej kolejności. Człowiek musiał zgadywać, który przycisk należy do
        której plakietki, a między nimi stało jeszcze CDC, którego nie da się wyłączyć.

        ⚠️ KOLEJNOŚĆ: replikacja SPS-a, potem natywny CT, na końcu CDC. Nie alfabetycznie ani nie
        „od najstarszego": od tego, co u klientów NAJWIĘCEJ KOSZTUJE, do tego, czego zwykle nie ma.
      */}

      {/* ── 1. REPLIKACJA SUPPLYPOINTA — u klientów odpowiada za cały ten koszt ───────────── */}
      <section className="space-y-1 rounded border border-border bg-muted/30 p-2 text-[11px] leading-snug">
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant={sps !== null && sps.jestCoWylaczac ? 'warning' : 'neutral'}>
            {sps === null
              ? t('sledz.spsNieMierzone')
              : !sps.jest
                ? t('sledz.spsNieMa')
                : sps.wierszy === null
                  ? t('sledz.spsJest').replace('{wierszy}', t('sledz.nieZmierzono'))
                  : sps.wierszy === 0
                    ? t('sledz.spsCzyste')
                    : t('sledz.spsJest').replace('{wierszy}', sps.wierszy.toLocaleString('pl-PL'))}
          </Badge>

          {/*
            ⚠️⚠️ PRZYCISK PRZY SWOJEJ PLAKIETCE, nie w osobnym rzędzie — o to poszło w zgłoszeniu.
            ZATRZYMUJE AUTOMAT, stąd `destructive` i osobne okienko z liczbami.
          */}
          {sps !== null && sps.jestCoWylaczac && agentId !== undefined && groupId !== undefined && (
            <Button
              size="sm"
              variant="destructive"
              className="ml-auto h-7"
              disabled={wylaczanie.isPending || cosLeci}
              title={powodJednoNaraz}
              onClick={() => setPytamOWylaczenie(true)}
            >
              {wylaczanie.isPending && <Loader2 className="size-3.5 animate-spin" />}
              {t('sledz.wylacz')}
            </Button>
          )}
          {/*
            ⚠️ BRAK PRZYCISKU MA POWÓD, i ekran go mówi. Wcześniej przycisk po prostu nie
            istniał — a na węźle, gdzie mechanizm był już wyłączony, wyglądało to jak brak funkcji.
          */}
          {sps !== null && sps.jest && !sps.jestCoWylaczac && (
            <span className="ml-auto text-muted-foreground">{t('sledz.spsNicDoRoboty')}</span>
          )}
        </div>

        {sps !== null && sps.jest && (
          <>
            <p className="text-foreground">
              {t('sledz.spsOpis').replace('{triggery}', String(sps.triggeryCzynne))}{' '}
              <span className="text-muted-foreground">{t('sledz.spsToNieCt')}</span>
            </p>

            {sps.naDobe !== null && sps.najstarszy !== null && (
              <p className="text-muted-foreground">
                {t('sledz.spsTempo')
                  .replace('{naDobe}', sps.naDobe.toLocaleString('pl-PL'))
                  .replace('{najstarszy}', new Date(sps.najstarszy).toLocaleDateString('pl-PL'))}
              </p>
            )}

            {/*
              ⚠️ TO JEST ZDANIE, PO KTÓRE SIĘ TU PRZYCHODZI. „Piszą i nikt nie odbiera" znaczy, że
              koszt jest ponoszony w całości bez żadnego zysku — i dopiero to czyni wyłączenie
              oczywistym. `null` (nie da się orzec) NIE dostaje żadnej z tych dwóch wersji.
            */}
            {sps.wProznie === true && <p className="text-[var(--warning)]">{t('sledz.spsWProznie')}</p>}
            {sps.wProznie === false && spsSurowe !== undefined && (
              <p className="text-muted-foreground">
                {t('sledz.spsOdbierane')
                  .replace('{relacji}', String(spsSurowe.relacji ?? '—'))
                  .replace('{przetworzonych}', (spsSurowe.przetworzonych ?? 0).toLocaleString('pl-PL'))}
              </p>
            )}

            {/*
              ⚠️ PROGRAMY KANAŁU ROZSTRZYGAJĄ „CZYNNE CZY SIEROTY", a tego nie widać w bazie.
              Usunięcie programów NIE zdejmuje triggerów, więc maszyna po wyłączeniu w połowie
              nadal płaci pełny koszt każdego zapisu. `null` = nie szukaliśmy (brak SupplyPointa).
            */}
            {sps.programowObecnych !== null && sps.programowWszystkich !== null && (
              <p className="text-muted-foreground">
                {t('sledz.spsProgramy')
                  .replace('{obecnych}', String(sps.programowObecnych))
                  .replace('{wszystkich}', String(sps.programowWszystkich))
                  .replace('{katalog}', spsSurowe?.katalogProgramow ?? '—')}
              </p>
            )}
            {sps.programowObecnych === 0 && sps.triggeryCzynne > 0 && (
              <p className="text-[var(--warning)]">{t('sledz.spsSieroty')}</p>
            )}
          </>
        )}

        {wylaczenie !== undefined && wylaczenie !== null && (
          <WynikWylaczeniaSpsSync zadanie={wylaczenie} juzWylaczone={sps !== null && !sps.jestCoWylaczac} />
        )}
      </section>

      {/* ── 2. NATYWNY CHANGE TRACKING MSSQL-A ───────────────────────────────────────────── */}
      <section className="space-y-1 rounded border border-border bg-muted/30 p-2 text-[11px] leading-snug">
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant={surowe.ct.wlaczony ? 'warning' : 'neutral'}>
            {t(surowe.ct.wlaczony ? 'sledz.ctWlaczony' : 'sledz.ctWylaczony')}
          </Badge>

          {/*
            ⚠️ TEN PRZYCISK NIE ZATRZYMUJE AUTOMATU — stąd `outline`, nie `destructive`. Zdjęcie CT
            to zmiana metadanych per tabela. Potwierdzenie jest i tak, bo cudza integracja
            czytająca `CHANGETABLE` padnie po tym bez śladu.
          */}
          {surowe.ct.wlaczony && agentId !== undefined && groupId !== undefined && (
            <Button
              size="sm"
              variant="outline"
              className="ml-auto h-7"
              disabled={wylaczanieCt.isPending || cosLeci}
              title={powodJednoNaraz}
              onClick={() => setPytamOCt(true)}
            >
              {wylaczanieCt.isPending && <Loader2 className="size-3.5 animate-spin" />}
              {t('sledz.wylaczCt')}
            </Button>
          )}
        </div>

        {surowe.ct.wlaczony ? (
          <>
            <p className="text-muted-foreground">
              {t('sledz.gdzie')
                .replace('{objete}', String(objete.length))
                .replace('{wszystkie}', String(surowe.tabelWSumie))
                .replace('{nasze}', String(p.tabelNaszych))}
            </p>
            <p className="text-muted-foreground">
              {t('sledz.ctSzczegoly')
                .replace('{retencja}', surowe.ct.retencja === null ? '—' : String(surowe.ct.retencja))
                .replace('{jednostka}', surowe.ct.jednostka ?? '—')
                .replace('{cleanup}', t(surowe.ct.autoCleanup === true ? 'common.enabled' : 'common.disabled'))}
            </p>
          </>
        ) : null}

        {wylaczenieCt !== undefined && wylaczenieCt !== null && (
          <WynikWylaczeniaSpsSync zadanie={wylaczenieCt} juzWylaczone={!surowe.ct.wlaczony} />
        )}
      </section>

      {/* ── 3. CDC ───────────────────────────────────────────────────────────────────────── */}
      <section className="space-y-1 rounded border border-border bg-muted/30 p-2 text-[11px] leading-snug">
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant={surowe.cdc.wlaczony ? 'destructive' : 'neutral'}>
            {t(surowe.cdc.wlaczony ? 'sledz.cdcWlaczony' : 'sledz.cdcWylaczony')}
          </Badge>
        </div>
        {/*
          ⚠️ CO TO JEST — pytanie padło wprost (03.09.2026: „czym jest ten CDC tutaj?"). Plakietka
          bez wyjaśnienia była trzecim skrótem obok dwóch mylących się nazw. Nie ma tu przycisku
          i nie będzie, dopóki nie zobaczymy CDC włączonego u kogokolwiek: jego wyłączenie zdejmuje
          zadania Agenta SQL i tabele w schemacie `cdc`, więc bez pierwszego przypadku pisalibyśmy
          procedurę na podstawie dokumentacji, a nie pomiaru.
        */}
        <p className="text-muted-foreground">{t('sledz.cdcCoTo')}</p>
      </section>

      {/*
        ══════════════════════════════════════════════════════════════════════════════════
         WYJŚCIE AWARYJNE — PRZERWIJ I PODNIEŚ INTERFEJS AUTOMATU.

         ⚠️⚠️ ZAMÓWIONE W TRAKCIE AWARII NA PRODUKCJI (09.09.2026): „musi być też bezwzględnie
         opcja przerwania tego, która od razu uruchamia SPS, bo to się potrafi zapętlić jeśli
         się nie skończy i teraz jest problem z wydawaniem w ogóle".

         ⚠️⚠️ POKAZUJEMY, GDY KTÓREKOLWIEK Z TYCH ZADAŃ BYŁO KIEDYKOLWIEK ZLECONE — nie tylko
         gdy coś trwa. Najgroźniejszy stan to właśnie ten, w którym portal NIC nie widzi
         w toku: agent zabity w środku `SPS_SYNC_WYLACZ` nie wykonał `finally` ze startem
         SPSUI, zadanie przeterminowało się, a automat stoi z zatrzymanym interfejsem
         i nie wydaje. Gdyby przycisk zależał od „coś trwa", nie byłoby go dokładnie wtedy,
         gdy jest jedynym wyjściem.

         ⚠️ NIE OBIECUJEMY „OD RAZU". Agent bierze jedno zadanie na raz i nie da się go
         przerwać w połowie — zdanie o tym, kiedy interfejs wstanie, przychodzi z serwera.
        ══════════════════════════════════════════════════════════════════════════════════
      */}
      {agentId !== undefined &&
        groupId !== undefined &&
        (wylaczenie != null || wylaczenieCt != null) && (
          <div className="flex flex-wrap items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              className="h-7"
              disabled={przerwanie.isPending}
              onClick={() => przerwanie.mutate({ agent: agentId, grupa: groupId })}
              title={t('sledz.przerwijOpis')}
            >
              {przerwanie.isPending && <Loader2 className="size-3.5 animate-spin" />}
              {t('sledz.przerwij')}
            </Button>
            {cosLeci && <span className="text-[11px] text-[var(--warning)]">{t('sledz.przerwijGdyTrwa')}</span>}
          </div>
        )}

      {/* ── STOPKA: rozmiar razem, tabele, czas pomiaru ─────────────────────────────────── */}
      <p className="text-[11px] leading-snug text-muted-foreground">{t('sledz.myNieUzywamy')}</p>

      {/* ⚠️ POWÓD BRAKU POMIARU MÓWIMY WPROST — inaczej „nie zmierzono" wygląda na naszą awarię. */}
      {p.bledy.length > 0 && (
        <ul className="space-y-0.5 text-[11px] text-[var(--warning)]">
          {p.bledy.map((b) => (
            <li key={b}>{b}</li>
          ))}
        </ul>
      )}

      <div className="flex flex-wrap items-center gap-2">
        {/*
          ⚠️ ROZMIAR JAKO PLAKIETKA W STOPCE, nie przy mechanizmie: to SUMA wszystkich trzech,
          więc przypisanie jej do któregokolwiek z osobna byłoby nieprawdą.
        */}
        <Badge variant="neutral">
          {p.bajtyRazem === null ? t('sledz.rozmiarNieznany') : t('sledz.rozmiar').replace('{ile}', mb(p.bajtyRazem))}
        </Badge>
        {/* Dopiero to są triggery, których nie umiemy przypisać do żadnego znanego mechanizmu. */}
        {p.triggeryInne !== undefined && p.triggeryInne > 0 && (
          <Badge variant="neutral">{t('sledz.triggeryInne').replace('{ile}', String(p.triggeryInne))}</Badge>
        )}
        <Button
          size="sm"
          variant="outline"
          className="h-7"
          disabled={doPokazania === 0}
          onClick={() => setTabeleOtwarte(true)}
        >
          {t('sledz.pokazTabele').replace('{n}', String(doPokazania))}
        </Button>
        <span className="text-[10px] text-muted-foreground">
          {t('sledz.zmierzono').replace('{kiedy}', new Date(zmierzono).toLocaleString('pl-PL'))}
        </span>
      </div>


      {pytamOCt && agentId !== undefined && groupId !== undefined && (
        <OknoPotwierdzenia
          tytul={t('sledz.wylaczCtTytul')}
          tresc={t('sledz.wylaczCtTresc')
            .replace('{wezel}', nodeId)
            .replace('{tabel}', String(p.tabelCt))}
          etykietaPotwierdz={t('sledz.wylaczCtPotwierdz')}
          ton="grozne"
          pracuje={wylaczanieCt.isPending}
          onPotwierdz={() => wylaczanieCt.mutate({ agent: agentId, grupa: groupId })}
          onAnuluj={() => setPytamOCt(false)}
        />
      )}

      {pytamOWylaczenie && agentId !== undefined && groupId !== undefined && (
        <OknoPotwierdzenia
          tytul={t('sledz.wylaczTytul')}
          tresc={t('sledz.wylaczTresc')
            .replace('{wezel}', nodeId)
            .replace('{wierszy}', (sps?.wierszy ?? 0).toLocaleString('pl-PL'))
            .replace('{triggery}', String(sps?.triggeryCzynne ?? 0))}
          etykietaPotwierdz={t('sledz.wylaczPotwierdz')}
          ton="grozne"
          pracuje={wylaczanie.isPending}
          onPotwierdz={() => wylaczanie.mutate({ agent: agentId, grupa: groupId })}
          onAnuluj={() => setPytamOWylaczenie(false)}
        />
      )}

      {tabeleOtwarte && (
        <Dialog open onOpenChange={(o) => !o && setTabeleOtwarte(false)}>
          <DialogContent
            className="w-[min(36rem,calc(100vw-2rem))]"
            title={t('sledz.tabeleTytul')}
            description={nodeId}
          >
            <div className="max-h-[60vh] space-y-4 overflow-y-auto">
              {objete.length > 0 && (
                <div className="rounded border border-border">
                  <table className="w-full text-sm">
                    <thead className="sticky top-0 bg-muted/70 text-left text-xs text-muted-foreground">
                      <tr>
                        <th className="p-2">{t('sledz.kolTabela')}</th>
                        <th className="p-2">{t('sledz.kolMechanizm')}</th>
                        <th className="p-2 text-right">{t('sledz.kolWierszy')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {objete.map((x) => (
                        <tr key={x.nazwa} className="border-t border-border">
                          <td className="p-2 font-mono text-[11px]">{x.nazwa}</td>
                          <td className="p-2">
                            <span className="flex flex-wrap gap-1">
                              {x.ct && <Badge variant="warning">CT</Badge>}
                              {x.cdc && <Badge variant="destructive">CDC</Badge>}
                            </span>
                          </td>
                          <td className="p-2 text-right tabular-nums">{x.wierszy.toLocaleString('pl-PL')}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/*
                TABELE MECHANIZMU SPS-A z rozmiarem każdej.
                ⚠️ Z ROZMIAREM, nie tylko z liczbą wierszy: to jedyne miejsce, w którym widać,
                że cały koszt siedzi w JEDNEJ tabeli, a pozostałe są puste.
              */}
              {spsSurowe !== undefined && spsSurowe.tabele.length > 0 && (
                <div>
                  <p className="mb-1 text-xs font-medium">{t('sledz.spsTabeleTytul')}</p>
                  <div className="rounded border border-border">
                    <table className="w-full text-sm">
                      <thead className="bg-muted/70 text-left text-xs text-muted-foreground">
                        <tr>
                          <th className="p-2">{t('sledz.kolTabela')}</th>
                          <th className="p-2 text-right">{t('sledz.kolWierszy')}</th>
                          <th className="p-2 text-right">{t('sledz.kolRozmiar')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {spsSurowe.tabele.map((x) => (
                          <tr key={x.nazwa} className="border-t border-border">
                            <td className="p-2 font-mono text-[11px]">{x.nazwa}</td>
                            <td className="p-2 text-right tabular-nums">
                              {x.wierszy === null ? t('sledz.nieZmierzono') : x.wierszy.toLocaleString('pl-PL')}
                            </td>
                            <td className="p-2 text-right tabular-nums">
                              {x.bajty === null ? t('sledz.nieZmierzono') : mb(x.bajty)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/*
                KTO PISZE — z próbki ostatnich wpisów, nie z całej historii.
                ⚠️ PRÓBKA JEST TU ZALETĄ, nie kompromisem: odpowiada na „kto pisze TERAZ", a nie
                „kto pisał przez dwanaście lat". Liczba wpisów w próbce stoi w tytule, żeby te
                liczby dały się czytać jako udziały.
              */}
              {sps !== null && sps.piszacy.length > 0 && (
                <div>
                  <p className="mb-1 text-xs font-medium">
                    {t('sledz.spsPiszacyTytul').replace('{probka}', sps.probka.toLocaleString('pl-PL'))}
                  </p>
                  <div className="rounded border border-border">
                    <table className="w-full text-sm">
                      <thead className="bg-muted/70 text-left text-xs text-muted-foreground">
                        <tr>
                          <th className="p-2">{t('sledz.kolTabela')}</th>
                          <th className="p-2 text-right">{t('sledz.kolWpisow')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sps.piszacy.map((x) => (
                          <tr key={x.tabela} className="border-t border-border">
                            <td className="p-2 font-mono text-[11px]">{x.tabela}</td>
                            <td className="p-2 text-right tabular-nums">{x.wpisow.toLocaleString('pl-PL')}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Triggery: nazwa mówi, do którego mechanizmu należy każdy z nich. */}
              {surowe.triggery.length > 0 && (
                <div>
                  <p className="mb-1 text-xs font-medium">{t('sledz.triggeryTytul')}</p>
                  <ul className="max-h-40 space-y-0.5 overflow-y-auto text-[11px] text-muted-foreground">
                    {surowe.triggery.map((tr) => (
                      <li key={`${tr.tabela}.${tr.nazwa}`}>
                        <span className="font-mono">{tr.tabela}</span> · {tr.nazwa}
                        {tr.wylaczony ? ` — ${t('sledz.triggerWylaczony')}` : ''}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setTabeleOtwarte(false)}>
                {t('common.close')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

/** Megabajty z jednym miejscem — rozmiary idą u klientów w setki MB, nie w bajty. */
function mb(bajty: number): string {
  return `${(bajty / 1024 / 1024).toFixed(1)} MB`;
}
